import { useEffect } from 'react';

/**
 * All the page-level motion there is: reveal-on-scroll for [data-reveal], a scrolled flag on the
 * header, and aria-current on the anchor nav. Reduced motion reveals everything immediately; the
 * CSS transition is also disabled there, so this hook never fights the preference.
 */
export function useSiteExperience() {
  useEffect(() => {
    const header = document.querySelector<HTMLElement>('[data-header]');
    const abort = new AbortController();
    const { signal } = abort;

    const paintHeader = () => header?.toggleAttribute('data-scrolled', window.scrollY > 12);
    window.addEventListener('scroll', paintHeader, { passive: true, signal });
    paintHeader();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealItems = [...document.querySelectorAll<HTMLElement>('[data-reveal]')];
    let revealObserver: IntersectionObserver | undefined;
    if (reduced || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.setAttribute('data-visible', ''));
    } else {
      revealObserver = new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.setAttribute('data-visible', '');
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
      );
      revealItems.forEach((item) => revealObserver?.observe(item));
    }

    const navLinks = [...document.querySelectorAll<HTMLAnchorElement>('[data-nav-link]')];
    let sectionObserver: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window && navLinks.length > 0) {
      sectionObserver = new IntersectionObserver(
        (entries) => {
          const active = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!active) return;
          navLinks.forEach((link) => {
            if (link.getAttribute('href') === `#${active.target.id}`) {
              link.setAttribute('aria-current', 'location');
            } else {
              link.removeAttribute('aria-current');
            }
          });
        },
        { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.2, 0.5] }
      );
      document
        .querySelectorAll<HTMLElement>('[data-section]')
        .forEach((section) => sectionObserver?.observe(section));
    }

    return () => {
      abort.abort();
      revealObserver?.disconnect();
      sectionObserver?.disconnect();
    };
  }, []);
}
